const de = {
  // ── Abschnitte, die von mehr als einer Seite genutzt werden ───────────────
  process: {
    eyebrow: "Unser Lernprozess",
    title: "Ein Lernweg, der sich nach Ihnen richtet",
    aside:
      "Jeder Lernende ist anders. Wir holen Sie dort ab, wo Sie stehen, und bauen einen Weg von Ihrer ersten Codezeile bis zur echten Praxis.",
    steps: [
      {
        title: "Einschätzung",
        body: "Wir schätzen ein, wo Sie heute stehen, und richten den Kurs auf Ihre Ziele aus, vom ersten Codeschreiben bis zum berufstätigen Entwickler.",
      },
      {
        title: "Lernen",
        body: "Live-Sitzungen mit viel Praxis, mit Begleitung in Echtzeit und Projekten aus der echten Entwicklungsarbeit.",
      },
      {
        title: "Souveränität",
        body: "Laufendes Feedback und Unterstützung, während Sie Ihre Fähigkeiten schärfen und sie eigenständig anzuwenden beginnen.",
      },
    ],
  },

  whyChoose: {
    eyebrow: "Warum wir",
    title: "Der Unterschied liegt in der Art, wie wir unterrichten",
    items: [
      {
        title: "Erfahrene Dozenten",
        body: "Berufstätige Software-Entwickler, die unterrichten. Jeder Dozent hat produktiven Code veröffentlicht und bringt diese Erfahrung in den Unterricht.",
      },
      {
        title: "Lernen in Ihrem Tempo",
        body: "Die Kurse passen sich Ihren Zielen und Ihrem Tempo an, sodass Sie Ihre Zeit dem Stoff widmen, der Sie voranbringt.",
      },
      {
        title: "Nachweisbare Ergebnisse",
        body: "Lernende gehen mit der Fähigkeit, etwas zu bauen. Das praxisnahe Format bedeutet, dass Sie die Fähigkeit üben, für die Sie gekommen sind, statt nur darüber zu lesen.",
      },
    ],
  },

  // ── Startseite ────────────────────────────────────────────────────────────
  home: {
    metaTitle: "SkillSynergy | Live-Kurse für Coding und KI",
    metaDescription:
      "Live-Programmier- und KI-Kurse mit viel Praxis, für jedes Niveau. Lernen Sie Software-Engineering Schritt für Schritt mit erfahrenen Dozenten, in Gruppen- oder Einzelunterricht.",

    hero: {
      badge: "Live-Onlinekurse",
      titleLine1: "Vom Anfänger",
      titleLine2: "zum Profi. Meistern Sie",
      titleCoding: "Coding",
      titleAnd: "&",
      titleAi: "KI",
      lead: "Live-Programmierkurse mit viel Praxis, für jedes Niveau, jetzt auch mit KI und maschinellem Lernen. Lernen Sie Schritt für Schritt mit erfahrenen Dozenten und gewinnen Sie echtes Selbstvertrauen.",
      rating: "Von Lernenden weltweit geschätzt",
      yearsValue: "10+",
      yearsLabel: "Jahre Erfahrung",
      imageAlt:
        "Ein Dozent begleitet einen Lernenden beim Programmieren am Laptop",
    },

    aiSpotlight: {
      badge: "Neuer Kurs",
      titlePre: "Lernen Sie",
      titleAi: "KI",
      titlePost: "und maschinelles Lernen",
      body: "Die gefragteste Fähigkeit der Tech-Branche, live und praxisnah unterrichtet. Von den Grundlagen des maschinellen Lernens bis zur Veröffentlichung echter Anwendungen mit LLMs und AI agents.",
      exploreCta: "Den KI-Kurs entdecken",
    },

    skills: {
      eyebrow: "Was Sie beherrschen werden",
      title: "Fähigkeiten, die echte Karrieren aufbauen",
    },

    pricing: {
      eyebrow: "Preise und Pakete",
      title: "Klare Pakete, ernsthafter Fortschritt",
    },

    cta: {
      title: "Bauen Sie echte Coding-Fähigkeiten mit Live-Kursen auf",
      body: "Buchen Sie eine kostenlose Beratung, und wir helfen Ihnen, den richtigen Weg zu wählen.",
    },
  },

  // ── Kursübersicht ─────────────────────────────────────────────────────────
  coursesPage: {
    metaTitle: "SkillSynergy | Kurse",
    metaDescription:
      "Individuelle und gruppenbasierte Online-Programmierkurse für alle Niveaus, über flexible, strukturierte Lernpfade mit unterschiedlicher Intensität.",

    eyebrow: "Unser Katalog",
    title: "Kurse für echten Fortschritt",
    lead: "Individuelle und gruppenbasierte Online-Programmierkurse für alle Niveaus, über flexible, strukturierte Lernpfade mit unterschiedlicher Intensität.",
    statCoursesValue: "7",
    statCoursesLabel: "Angebotene Kurse",
    statYearsValue: "10+",
    statYearsLabel: "Jahre Erfahrung",
    coreTitle: "Unsere Kernkurse",
    heroImageAlt: "Lernende arbeiten gemeinsam an einer Programmieraufgabe",
  },

  // ── Kursdetail ────────────────────────────────────────────────────────────
  coursePage: {
    curriculumEyebrow: "Lehrplan",
    curriculumTitle: "Was Sie lernen",
    aboutTitle: "Über diesen Kurs",
    exploreOthers: "Weitere Kurse entdecken",
    readyTitle: (course: string) => `Bereit, mit ${course} zu starten?`,
    readyBody:
      "Buchen Sie eine kostenlose Beratung und erhalten Sie einen individuellen Plan.",
  },

  // ── Über uns ──────────────────────────────────────────────────────────────
  about: {
    metaTitle: "SkillSynergy | Über uns",
    metaDescription:
      "SkillSynergy bietet interaktive Live-Programmierkurse für Lernende auf jedem Niveau, auch für absolute Anfänger. Kleine Gruppen, unterrichtet von berufstätigen Entwicklern.",

    eyebrow: "Über SkillSynergy",
    title: "Wir bringen Menschen bei, echte Software zu schreiben",
    lead: "Interaktive Live-Programmierkurse für Lernende aller Niveaus, auch von null an.",
    storyEyebrow: "Über SkillSynergy",
    storyTitle: "Ihr verlässlicher Begleiter beim Programmieren",
    storyBody1:
      "SkillSynergy bietet interaktive Live-Programmierkurse für Lernende auf jedem Niveau, auch für absolute Anfänger. Die Gruppen sind klein, praxisnah und werden von berufstätigen Entwicklern unterrichtet.",
    storyBody2:
      "Das Ziel ist einfach: Sie können am Ende eigenständig Software bauen, und Sie haben jemanden zum Fragen, wenn Sie nicht weiterkommen.",
    cta: {
      title: "Lassen Sie uns gemeinsam Ihre Fähigkeiten aufbauen",
      body: "Lernen Sie zusammen mit anderen, mit fachkundiger Begleitung.",
    },
    imageAlt: [
      "Ein Lernender beim Pair-Programming am Laptop",
      "Ein Dozent bespricht Code am Whiteboard",
      "Eine Lerngruppe arbeitet an einer Übung",
    ],
    storyImageAlt: "Ein Entwickler löst ein Problem am Laptop",
  },

  // ── Preise ────────────────────────────────────────────────────────────────
  pricingPage: {
    metaTitle: "SkillSynergy | Preise",
    metaDescription:
      "Wählen Sie einen Gruppenkurs für gemeinsames Lernen oder gehen Sie den individuellen Weg für vollständig persönliche Betreuung. Die Ausrichtungen Light, Semi-Intensiv und Intensiv.",

    offerBadge: (offer: string) =>
      `Einführungsangebot · ${offer}% Rabatt für die ersten Kurse`,
    title: "Die besten Preise für Sie",
    lead: "Wählen Sie einen Gruppenkurs für gemeinsames Lernen oder gehen Sie den individuellen Weg für vollständig persönliche Betreuung.",
    perMonth: "/Mon.",
    priceNote:
      "Alle Preise gelten pro Monat und enthalten die gesetzliche MwSt., soweit anwendbar. Keine Anmeldegebühr.",
    popular: "Beliebt",
    group: "Gruppe",
    individual: "Einzeln",
    planLabel: "Abrechnungsmodell",
    cta: {
      title: "Unsicher, welches Paket passt?",
      body: "Buchen Sie eine kostenlose Beratung, und wir empfehlen die passende Ausrichtung für Ihre Ziele.",
    },
  },

  // ── Kontakt ───────────────────────────────────────────────────────────────
  contact: {
    metaTitle: "SkillSynergy | Kontakt",
    metaDescription:
      "Nehmen Sie Kontakt auf, und unser Team hilft Ihnen, den richtigen Kurs zu finden und loszulegen. Wir antworten in der Regel innerhalb eines Werktags.",

    eyebrow: "Kontakt aufnehmen",
    title: "Möchten Sie mehr über unsere Kurse erfahren?",
    lead: "Melden Sie sich, und unser Team hilft Ihnen, den richtigen Kurs zu finden und loszulegen. Wir antworten in der Regel innerhalb eines Werktags.",
    phone: "Telefon",
    email: "E-Mail",
    location: "Standort",
    formTitle: "Schreiben Sie uns eine Nachricht",
    fullName: "Vollständiger Name",
    fullNamePlaceholder: "Erika Mustermann",
    emailLabel: "E-Mail",
    emailPlaceholder: "erika@email.com",
    subject: "Betreff",
    subjectPlaceholder: "Kursanfrage",
    message: "Nachricht",
    messagePlaceholder: "Erzählen Sie uns von Ihren Zielen...",
    send: "Nachricht senden",
    sending: "Wird gesendet…",
    successTitle: "Nachricht gesendet",
    successBody:
      "Danke für Ihre Nachricht. Wir antworten innerhalb eines Werktags.",
    error:
      "Das hat nicht geklappt. Bitte versuchen Sie es erneut oder schreiben Sie uns direkt.",
  },
};

export default de;
